import {
  Node,
  mergeAttributes,
  ReactNodeViewRenderer,
} from "@tiptap/react";
import { NodeRange } from "@tiptap/pm/model";
import { findWrapping } from "@tiptap/pm/transform";
import SectionNodeView from "./SectionNodeView";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    section: {
      insertSection: (attrs: { kind: string }) => ReturnType;
    };
  }
}

const Section = Node.create({
  name: "section",
  group: "block",
  content: "paragraph+",
  defining: true,

  addAttributes() {
    return {
      kind: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-section-kind"),
        renderHTML: (attributes) => {
          if (!attributes.kind) return {};
          return { "data-section-kind": attributes.kind };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "section[data-section-kind]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["section", mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      insertSection:
        ({ kind }) =>
        ({ commands, state, tr, dispatch }) => {
          const sectionType = state.schema.nodes[this.name];
          const sectionAttrs = { kind };
          const emptySectionNode = {
            type: this.name,
            attrs: sectionAttrs,
            content: [{ type: "paragraph" }],
          };

          const { $from, $to, empty } = state.selection;

          for (let depth = $from.depth; depth > 0; depth--) {
            if ($from.node(depth).type.name === this.name) {
              const sectionDepth = depth;
              const atFirstChild = $from.index(sectionDepth) === 0;
              const atParagraphStart = $from.parentOffset === 0;

              if (dispatch) {
                if (atFirstChild && atParagraphStart) {
                  tr.setNodeMarkup(
                    $from.before(sectionDepth),
                    null,
                    sectionAttrs,
                  );
                } else if (atParagraphStart) {
                  tr.split($from.before($from.depth), 1, [
                    { type: sectionType, attrs: sectionAttrs },
                  ]);
                } else {
                  tr.split($from.pos, 2, [
                    null,
                    { type: sectionType, attrs: sectionAttrs },
                  ]);
                }
              }
              return true;
            }
          }

          let range: NodeRange | null = null;

          if (empty && $from.depth >= 1) {
            const doc = state.doc;
            const startIndex = $from.index(0);

            if (startIndex < doc.childCount) {
              let endIndex = doc.childCount;
              for (let i = startIndex; i < doc.childCount; i++) {
                if (doc.child(i).type === sectionType) {
                  endIndex = i;
                  break;
                }
              }

              if (endIndex > startIndex) {
                let startPos = 0;
                for (let i = 0; i < startIndex; i++) {
                  startPos += doc.child(i).nodeSize;
                }
                let endPos = startPos;
                for (let i = startIndex; i < endIndex; i++) {
                  endPos += doc.child(i).nodeSize;
                }

                const $rangeStart = doc.resolve(startPos + 1);
                const $rangeEnd = doc.resolve(endPos - 1);
                range = new NodeRange($rangeStart, $rangeEnd, 0);
              }
            }
          } else {
            range = $from.blockRange($to);
          }

          if (range) {
            const wrapping = findWrapping(range, sectionType, sectionAttrs);
            if (wrapping) {
              if (dispatch) tr.wrap(range, wrapping);
              return true;
            }
          }

          return commands.insertContent(emptySectionNode);
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(SectionNodeView);
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ state, dispatch, tr }) => {
          const { $from, empty } = state.selection;
          if (!empty || $from.parentOffset !== 0) return false;

          let sectionDepth = -1;
          for (let depth = $from.depth; depth > 0; depth--) {
            if ($from.node(depth).type.name === this.name) {
              sectionDepth = depth;
              break;
            }
          }
          if (sectionDepth === -1) return false;
          if ($from.index(sectionDepth) !== 0) return false;

          if (dispatch) {
            const sectionStart = $from.before(sectionDepth);
            const sectionEnd = $from.after(sectionDepth);
            const sectionNode = $from.node(sectionDepth);
            tr.replaceWith(sectionStart, sectionEnd, sectionNode.content);
          }
          return true;
        }),
    };
  },
});

export default Section;
